import classNames from "classnames";
import React, { FC, ReactNode } from "react";

import { avatarColor, fileKind, firmInitials, initials } from "../portal.utils";

export const portalInput =
  "h-11 w-full rounded-[9px] border-[1.5px] border-[#DCE2EA] bg-white px-3.5 text-sm font-semibold text-[#1F2433] outline-none placeholder:font-medium placeholder:text-[#9097A8] focus:border-[#6B4EFF]";
export const portalLabel = "text-[13px] font-bold text-[#3A4256]";
export const portalPrimary =
  "flex h-[46px] w-full items-center justify-center rounded-[9px] bg-[#6B4EFF] text-[14.5px] font-extrabold text-white shadow-[0_3px_10px_rgba(107,78,255,0.3)] hover:bg-[#5B3DF5] disabled:cursor-not-allowed disabled:opacity-60";
export const portalLink = "font-bold text-[#6B4EFF] hover:text-[#4B2FC7]";

/** The firm's square badge (initials until firms can add a logo). */
export const FirmBadge: FC<{ name: string; size?: "lg" | "md" | "sm" }> = ({
  name,
  size = "lg",
}) => (
  <span
    className={classNames(
      "flex shrink-0 items-center justify-center bg-[#1F2433] font-extrabold text-white",
      size === "lg" && "h-11 w-11 rounded-xl text-base",
      size === "md" && "h-9 w-9 rounded-[10px] text-[13px]",
      size === "sm" && "h-8 w-8 rounded-[9px] text-[11.5px]",
    )}>
    {firmInitials(name)}
  </span>
);

export const Avatar: FC<{ name: string; size?: number; square?: boolean }> = ({
  name,
  size = 36,
  square,
}) => (
  <span
    className={classNames(
      "flex shrink-0 items-center justify-center font-extrabold text-white",
      square ? "rounded-md" : "rounded-full",
    )}
    style={{
      width: size,
      height: size,
      background: avatarColor(name),
      fontSize: Math.round(size * 0.36),
    }}>
    {initials(name)}
  </span>
);

export const FileBadge: FC<{ name: string }> = ({ name }) => {
  const kind = fileKind(name);
  return (
    <span
      className={
        "flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg text-[10px] font-extrabold"
      }
      style={{ background: kind.bg, color: kind.fg }}>
      {kind.label}
    </span>
  );
};

export const PortalAlert: FC<{
  children: ReactNode;
  tone?: "error" | "info" | "success";
}> = ({ children, tone = "error" }) => (
  <div
    role={tone === "error" ? "alert" : "status"}
    className={classNames(
      "rounded-[9px] px-3.5 py-2.5 text-[13px] font-semibold leading-relaxed",
      tone === "error" && "bg-[#FDECEC] text-[#B42318]",
      tone === "info" && "bg-[#F1EEFC] text-[#4B2FC7]",
      tone === "success" && "bg-[#E9F9F1] text-[#147A4B]",
    )}>
    {children}
  </div>
);

const LockIcon = () => (
  <svg
    width={"14"}
    height={"14"}
    viewBox={"0 0 24 24"}
    fill={"none"}
    stroke={"currentColor"}
    strokeWidth={"2"}
    strokeLinecap={"round"}
    strokeLinejoin={"round"}
    aria-hidden={"true"}>
    <rect x={"4"} y={"11"} width={"16"} height={"10"} rx={"2"} />
    <path d={"M8 11V7a4 4 0 0 1 8 0v4"} />
  </svg>
);

/** Sign-in pages: firm name on top, a card in the middle, the security note below. */
export const PortalAuthFrame: FC<{
  firmName?: string | null;
  children: ReactNode;
}> = ({ firmName, children }) => (
  <div
    className={
      "flex min-h-screen flex-col items-center justify-center gap-7 bg-[#F7F8FA] px-4 py-10 font-[Inter,sans-serif] text-[#1F2433]"
    }>
    <div className={"flex items-center gap-3"}>
      <FirmBadge name={firmName || "ProCharted"} />
      <div className={"flex flex-col"}>
        <span className={"text-lg font-extrabold"}>
          {firmName || "Client portal"}
        </span>
        <span className={"text-[12.5px] font-semibold text-[#6B7185]"}>
          Secure client portal
        </span>
      </div>
    </div>
    <div
      className={
        "flex w-full max-w-[440px] flex-col gap-[22px] rounded-2xl border border-[#E8E8EC] bg-white p-6 shadow-[0_8px_30px_rgba(31,36,51,0.06)] sm:p-8"
      }>
      {children}
    </div>
    <div
      className={
        "flex max-w-[440px] items-center gap-2 text-center text-[12.5px] font-semibold text-[#6B7185]"
      }>
      <LockIcon />
      <span>
        Files are encrypted and only shared with {firmName || "your accountant"}{" "}
        · Powered by ProCharted
      </span>
    </div>
  </div>
);
